import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { forumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MessageSquare, Plus, User, Loader2, ChevronRight, Eye, MessageCircle, Truck, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BacklotPage = () => {
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({ category: '', title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);

  const tabs = [
    { id: 'general', label: 'General Discussions' },
    { id: 'dispatch_help', label: 'LoadBoard Support' },
    { id: 'carrier_lounge', label: 'Networking' },
    { id: 'fraud_alerts', label: 'Industry Alerts' },
  ];

  const trendingTopics = [
    { label: 'Spot Market Trends', color: 'dot-orange' },
    { label: 'Avoiding Freight Scams', color: 'dot-yellow' },
    { label: 'Best GPS Apps for Truckers', color: 'dot-green' },
  ];

  const recentPosts = [
    { title: 'Anyone in Dallas today?', author: 'User' },
    { title: 'Need a backhaul to Florida!', author: 'User' },
    { title: 'Looking for Flatbed Loads', author: 'User' },
  ];

  const categories = [
    { value: 'fraud_alerts', label: 'Industry Alerts' },
    { value: 'dispatch_help', label: 'LoadBoard Support' },
    { value: 'carrier_lounge', label: 'Networking' },
    { value: 'broker_talk', label: 'General Discussions' },
  ];

  useEffect(() => {
    loadPosts();
  }, [activeTab]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'general') {
        params.category = activeTab;
      }
      const response = await forumAPI.getPosts(params);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.category || !newPost.title || !newPost.body) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await forumAPI.createPost(newPost);
      toast.success('Post created!');
      setCreateDialogOpen(false);
      setNewPost({ category: '', title: '', body: '' });
      loadPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-12">
      {/* Hero Banner with Car Carrier Image */}
      <div 
        className="relative h-[420px] bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/937dade9-da0e-42b3-b2b9-a7233decd77c/images/7c6b4b0ef5e80a775306b02a0af6beb24d0cdc0589b0b0bd7b721651a7814165.png)',
        }}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/90 via-[#0D1B2A]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A] via-transparent to-[#0D1B2A]/30" />
        
        {/* Content */}
        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
          <div className="max-w-2xl">
            {/* Logo and Title */}
            <div className="flex items-center gap-4 mb-4">
              <svg width="48" height="54" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="heroBacklotShieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1E3A5F" />
                    <stop offset="40%" stopColor="#3B82F6" />
                    <stop offset="70%" stopColor="#60A5FA" />
                    <stop offset="100%" stopColor="#94A3B8" />
                  </linearGradient>
                  <linearGradient id="heroBacklotInnerShield" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#1E3A5F" />
                    <stop offset="100%" stopColor="#0F172A" />
                  </linearGradient>
                  <linearGradient id="heroBacklotBlueBar" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#38BDF8" />
                    <stop offset="30%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </linearGradient>
                  <linearGradient id="heroBacklotGreenBar" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4ADE80" />
                    <stop offset="30%" stopColor="#22C55E" />
                    <stop offset="100%" stopColor="#15803D" />
                  </linearGradient>
                </defs>
                <path d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" fill="url(#heroBacklotShieldBorder)"/>
                <path d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" fill="url(#heroBacklotInnerShield)"/>
                <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#heroBacklotBlueBar)" />
                <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#heroBacklotGreenBar)" />
                <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
              </svg>
              <h1 className="text-4xl md:text-5xl font-bold" style={{fontFamily: 'Poppins, sans-serif'}}>
                <span className="text-white">HaulLogix </span>
                <span className="text-[#4ADE80]">Backlot</span>
              </h1>
            </div>
            
            {/* Tagline */}
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              <span className="text-[#4ADE80] font-semibold">Connect</span>
              <span className="text-gray-400 mx-2">•</span>
              <span className="text-[#4ADE80] font-semibold">Learn</span>
              <span className="text-gray-400 mx-2">•</span>
              <span className="text-[#4ADE80] font-semibold">Grow</span>
              <span className="text-gray-300"> with the Auto Transport Network</span>
            </p>
            
            {/* Feature Pills */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#112240]/80 border border-[#3B82F6]/30 rounded-full backdrop-blur-sm">
                <MessageSquare className="w-4 h-4 text-[#60A5FA]" />
                <span className="text-white text-sm font-medium">Discussions</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#112240]/80 border border-[#4ADE80]/30 rounded-full backdrop-blur-sm">
                <Truck className="w-4 h-4 text-[#4ADE80]" />
                <span className="text-white text-sm font-medium">Carrier Tips</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#112240]/80 border border-[#3B82F6]/30 rounded-full backdrop-blur-sm">
                <TrendingUp className="w-4 h-4 text-[#60A5FA]" />
                <span className="text-white text-sm font-medium">Market Intel</span>
              </div>
            </div>

            {/* New Post Button - for authenticated users */}
            {isAuthenticated && (
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="mt-6 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black font-semibold px-6"
                style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                data-testid="create-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-[#3B82F6]/20 pb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-white border-b-2 border-[#4ADE80]'
                      : 'text-gray-500 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Posts List */}
            <div className="bg-[#112240] rounded-lg border border-[#3B82F6]/20 overflow-hidden" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#4ADE80]" />
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-500">No posts yet. Start a discussion!</p>
                </div>
              ) : (
                posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/backlot/${post.id}`}
                    className="flex items-center gap-4 p-4 border-b border-[#3B82F6]/10 hover:bg-[#162A4A] transition-colors"
                    data-testid={`post-${post.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0D1B2A] flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{post.title}</h3>
                      <p className="text-sm text-gray-500">
                        Started by <span className="text-[#4ADE80]">{post.author_profile?.full_name || 'User'}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        {post.reply_count} replies
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {Math.floor(Math.random() * 10) + 1}k views
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Trending Topics */}
            <div className="bg-[#112240] rounded-lg border border-[#3B82F6]/20 p-4" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-white">Trending Topics</h3>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </div>
              <div className="space-y-2">
                {trendingTopics.map((topic, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${topic.color}`}></div>
                    <span className="text-gray-300 hover:text-white cursor-pointer">{topic.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Find a Partner */}
            <div className="bg-[#112240] rounded-lg border border-[#3B82F6]/20 p-4" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <h3 className="font-semibold text-white mb-3">Find a Partner</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start h-9 text-sm border-[#3B82F6]/20 text-gray-300 hover:text-white hover:bg-[#162A4A]">
                  Carrier Connections
                </Button>
                <Button variant="outline" className="w-full justify-start h-9 text-sm border-[#3B82F6]/20 text-gray-300 hover:text-white hover:bg-[#162A4A]">
                  Broker Network
                </Button>
              </div>
            </div>

            {/* Recent Posts */}
            <div className="bg-[#112240] rounded-lg border border-[#3B82F6]/20 p-4" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-white">Recent Posts</h3>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </div>
              <div className="space-y-3">
                {recentPosts.map((post, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#0D1B2A] flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-400 hover:text-white cursor-pointer">{post.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)'}}>
          <DialogHeader>
            <DialogTitle className="text-white">Create New Post</DialogTitle>
            <DialogDescription className="text-gray-500">
              Share your thoughts with the community
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Category</label>
              <Select
                value={newPost.category}
                onValueChange={(value) => setNewPost({ ...newPost, category: value })}
              >
                <SelectTrigger className="bg-[#0D1B2A] border-[#3B82F6]/20 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-gray-300">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Title</label>
              <Input
                placeholder="What's your topic?"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                className="bg-[#0D1B2A] border-[#3B82F6]/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Content</label>
              <Textarea
                placeholder="Share your thoughts..."
                value={newPost.body}
                onChange={(e) => setNewPost({ ...newPost, body: e.target.value })}
                rows={5}
                className="bg-[#0D1B2A] border-[#3B82F6]/20 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-[#3B82F6]/20 text-gray-300">
              Cancel
            </Button>
            <Button
              onClick={handleCreatePost}
              disabled={submitting}
              className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black"
              style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                'Post'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BacklotPage;
